/**
 * V4.43 — Éditeur de segmentation apporteur (utilisé dans le drawer).
 *
 * Affiche :
 *   - Toggle catégorie (3 boutons : Pro / Particulier / Non renseigné)
 *   - Champs conditionnels selon la catégorie :
 *       * Pro       : activity, company, role, zone
 *       * Particulier : relation (select) + champ "autre" texte si "autre"
 *       * Non renseigné : aucun champ supplémentaire
 *   - Bouton "Enregistrer" (apparaît dès qu'un champ a été modifié)
 *
 * UX : optimiste, toast sonner sur succès / erreur. Re-fetch parent au save.
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Check, Briefcase, User, Question, FloppyDisk, X } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import {
  APPORTEUR_CATEGORIES,
  BUSINESS_ACTIVITIES,
  PERSONAL_RELATIONS,
} from "@/lib/apporteurSegmentation";

const CATEGORY_ICONS = {
  professionnel: Briefcase,
  particulier: User,
  non_renseigne: Question,
};

function initFormFromApporteur(a) {
  return {
    category: a?.category || "non_renseigne",
    business_activity: a?.business_activity || "",
    company_name: a?.company_name || "",
    professional_role: a?.professional_role || "",
    zone: a?.zone || "",
    personal_relation: a?.personal_relation || "",
    personal_relation_other: a?.personal_relation_other || "",
    // V4.44 — Tracking introductions
    can_introduce: a?.can_introduce || "",
  };
}

function isDirty(initial, current) {
  return (
    initial.category !== current.category ||
    initial.business_activity !== current.business_activity ||
    initial.company_name !== current.company_name ||
    initial.professional_role !== current.professional_role ||
    initial.zone !== current.zone ||
    initial.personal_relation !== current.personal_relation ||
    initial.personal_relation_other !== current.personal_relation_other ||
    initial.can_introduce !== current.can_introduce
  );
}

export default function ApporteurSegmentationEditor({ apporteur, onSaved }) {
  const [initial, setInitial] = useState(() => initFormFromApporteur(apporteur));
  const [form, setForm] = useState(() => initFormFromApporteur(apporteur));
  const [saving, setSaving] = useState(false);

  // Re-init si l'apporteur change (changement de drawer)
  useEffect(() => {
    const fresh = initFormFromApporteur(apporteur);
    setInitial(fresh);
    setForm(fresh);
  }, [apporteur?.name]);

  const dirty = isDirty(initial, form);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!apporteur?.name || !dirty) return;
    setSaving(true);
    // Si on change de catégorie, on vide les champs de l'autre famille pour ne pas
    // laisser de données fantômes.
    const payload = { ...form };
    if (form.category !== "professionnel") {
      payload.business_activity = "";
      payload.company_name = "";
      payload.professional_role = "";
      payload.zone = "";
      payload.can_introduce = "";  // V4.44 — Le tracking intro n'a de sens que pour les Pros
    }
    if (form.category !== "particulier") {
      payload.personal_relation = "";
      payload.personal_relation_other = "";
    }
    if (payload.personal_relation !== "autre") {
      payload.personal_relation_other = "";
    }
    try {
      const url = `/apporteurs/${encodeURIComponent(apporteur.name)}`;
      const { data } = await api.patch(url, payload);
      const fresh = initFormFromApporteur(data);
      setInitial(fresh);
      setForm(fresh);
      toast.success("Segmentation mise à jour");
      onSaved?.(data);
    } catch (err) {
      console.error("Segmentation save error:", err);
      const msg = err?.response?.data?.detail || "Erreur lors de l'enregistrement";
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const cancel = () => {
    setForm(initial);
  };

  const cat = form.category;

  return (
    <div className="border border-slate-200 rounded-xl p-3 bg-slate-50/40" data-testid="apporteur-segmentation-editor">
      <div className="text-[9px] font-bold uppercase tracking-widest text-slate-400 mb-2">
        Segmentation
      </div>

      {/* Toggle catégorie */}
      <div className="grid grid-cols-3 gap-1.5 mb-3" role="radiogroup" aria-label="Catégorie d'apporteur">
        {APPORTEUR_CATEGORIES.map((c) => {
          const Icon = CATEGORY_ICONS[c.key];
          const active = cat === c.key;
          return (
            <button
              key={c.key}
              type="button"
              role="radio"
              aria-checked={active}
              onClick={() => setField("category", c.key)}
              className={`px-2 py-2 rounded-lg text-[11px] font-extrabold border transition-all flex items-center justify-center gap-1.5 ${
                active ? "shadow-sm" : "opacity-70 hover:opacity-100"
              }`}
              style={{
                background: active ? c.bg : "#FFFFFF",
                color: active ? c.color : "var(--cf-neutral-text)",
                borderColor: active ? c.border : "var(--cf-border)",
              }}
              data-testid={`segment-category-${c.key}`}
            >
              <Icon size={12} weight={active ? "fill" : "regular"} />
              <span className="truncate">{c.label}</span>
              {active && <Check size={11} weight="bold" />}
            </button>
          );
        })}
      </div>

      {/* Champs conditionnels — Professionnel */}
      {cat === "professionnel" && (
        <div className="space-y-2" data-testid="segment-pro-fields">
          <Field label="Activité">
            <select
              value={form.business_activity}
              onChange={(e) => setField("business_activity", e.target.value)}
              className="w-full px-2 py-1.5 rounded-lg border border-slate-200 text-[12px] font-semibold bg-white focus:outline-none focus:border-[color:var(--cf-primary)] focus:ring-1 focus:ring-[color:var(--cf-primary-border)]"
              data-testid="segment-business-activity"
            >
              <option value="">— Sélectionner —</option>
              {BUSINESS_ACTIVITIES.map((b) => (
                <option key={b.key} value={b.key}>{b.label}</option>
              ))}
            </select>
          </Field>

          <Field label="Entreprise / Réseau">
            <input
              type="text"
              value={form.company_name}
              onChange={(e) => setField("company_name", e.target.value)}
              placeholder="Ex : Orpi, Stéphane Plaza Immobilier, Cabinet X…"
              className="w-full px-2 py-1.5 rounded-lg border border-slate-200 text-[12px] font-semibold bg-white focus:outline-none focus:border-[color:var(--cf-primary)] focus:ring-1 focus:ring-[color:var(--cf-primary-border)]"
              data-testid="segment-company-name"
            />
          </Field>

          <div className="grid grid-cols-2 gap-2">
            <Field label="Fonction / rôle">
              <input
                type="text"
                value={form.professional_role}
                onChange={(e) => setField("professional_role", e.target.value)}
                placeholder="Ex : directeur d'agence"
                className="w-full px-2 py-1.5 rounded-lg border border-slate-200 text-[12px] font-semibold bg-white focus:outline-none focus:border-[color:var(--cf-primary)] focus:ring-1 focus:ring-[color:var(--cf-primary-border)]"
                data-testid="segment-professional-role"
              />
            </Field>

            <Field label="Zone géographique">
              <input
                type="text"
                value={form.zone}
                onChange={(e) => setField("zone", e.target.value)}
                placeholder="Ex : Béziers, Hérault"
                className="w-full px-2 py-1.5 rounded-lg border border-slate-200 text-[12px] font-semibold bg-white focus:outline-none focus:border-[color:var(--cf-primary)] focus:ring-1 focus:ring-[color:var(--cf-primary-border)]"
                data-testid="segment-zone"
              />
            </Field>
          </div>

          {/* V4.44 — Tracking introductions / présentations */}
          <div className="mt-2 pt-2 border-t border-slate-200" data-testid="segment-intro-block">
            <div className="flex items-center justify-between mb-1.5">
              <div className="text-[9px] font-bold uppercase tracking-widest text-slate-400">
                Présentations
              </div>
              {/* V4.45 — Compteur d'introductions obtenues (auto + manuel) */}
              {(apporteur?.intro_obtained_count_total ?? 0) > 0 && (
                <div
                  className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-extrabold"
                  style={{ background: "var(--cf-success-soft)", color: "var(--cf-success-text)", border: "1px solid var(--cf-success-border)" }}
                  title={`Auto (via parrain) : ${apporteur.intro_obtained_count_auto || 0} · Manuel : ${apporteur.intro_obtained_count_manual || 0}`}
                  data-testid="intro-obtained-counter"
                >
                  🤝 {apporteur.intro_obtained_count_total} obtenue{apporteur.intro_obtained_count_total > 1 ? "s" : ""}
                </div>
              )}
            </div>
            <div className="text-[10.5px] text-slate-500 font-semibold leading-snug mb-2">
              Peut me présenter d&apos;autres collaborateurs ?
            </div>
            <div className="grid grid-cols-3 gap-1.5">
              {[
                { key: "oui", label: "Oui", emoji: "✋", color: "var(--cf-success-text)", bg: "var(--cf-success-soft)", border: "var(--cf-success-border)" },
                { key: "non", label: "Non", emoji: "❌", color: "var(--cf-danger-text)", bg: "var(--cf-danger-soft)", border: "var(--cf-danger-border)" },
                { key: "deja_demande", label: "Déjà demandé", emoji: "⏳", color: "var(--cf-warning-text)", bg: "var(--cf-warning-soft)", border: "var(--cf-warning-border)" },
              ].map((opt) => {
                const active = form.can_introduce === opt.key;
                return (
                  <button
                    key={opt.key}
                    type="button"
                    onClick={() => setField("can_introduce", active ? "" : opt.key)}
                    className={`px-1.5 py-1.5 rounded-lg text-[10.5px] font-extrabold border transition-all leading-tight ${
                      active ? "shadow-sm" : "opacity-65 hover:opacity-100"
                    }`}
                    style={{
                      background: active ? opt.bg : "#FFFFFF",
                      color: active ? opt.color : "var(--cf-neutral-text)",
                      borderColor: active ? opt.border : "var(--cf-border)",
                    }}
                    data-testid={`segment-can-introduce-${opt.key}`}
                  >
                    <span className="mr-0.5">{opt.emoji}</span>
                    {opt.label}
                  </button>
                );
              })}
            </div>
            {form.can_introduce === "deja_demande" && apporteur?.last_intro_request_date && (
              <div className="text-[10px] text-amber-700 font-semibold mt-1.5" data-testid="segment-intro-last-date">
                ⏳ Demandé le {apporteur.last_intro_request_date}
              </div>
            )}
            {form.can_introduce === "deja_demande" && !apporteur?.last_intro_request_date && (
              <div className="text-[10px] text-slate-500 font-semibold mt-1.5">
                ℹ La date sera enregistrée automatiquement à l&apos;enregistrement.
              </div>
            )}

            {/* V4.45 — Bouton +1 intro manuel (hors flow dirty/save : action atomique) */}
            <div className="mt-2 flex items-center gap-2">
              <button
                type="button"
                onClick={async () => {
                  if (!apporteur?.name) return;
                  try {
                    const url = `/apporteurs/${encodeURIComponent(apporteur.name)}/intro-obtained`;
                    const { data } = await api.post(url);
                    toast.success(`Intro #${data.intro_obtained_count_manual} enregistrée pour ${apporteur.name}`);
                    onSaved?.({ ...apporteur, intro_obtained_count_manual: data.intro_obtained_count_manual });
                  } catch (err) {
                    console.error("intro-obtained error:", err);
                    toast.error("Impossible d'incrémenter le compteur");
                  }
                }}
                className="flex-1 inline-flex items-center justify-center gap-1 px-2 py-1.5 rounded-lg text-[10.5px] font-extrabold bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100 transition-colors"
                title="Enregistre +1 intro même si le nouvel apporteur n'a pas encore été créé dans Crédit Flow"
                data-testid="intro-obtained-increment"
              >
                🤝 +1 intro obtenue
              </button>
              <div className="text-[9.5px] text-slate-400 font-semibold leading-snug flex-1">
                Auto via parrain : {apporteur?.intro_obtained_count_auto || 0} · Manuel : {apporteur?.intro_obtained_count_manual || 0}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Champs conditionnels — Particulier */}
      {cat === "particulier" && (
        <div className="space-y-2" data-testid="segment-particulier-fields">
          <Field label="Relation">
            <select
              value={form.personal_relation}
              onChange={(e) => setField("personal_relation", e.target.value)}
              className="w-full px-2 py-1.5 rounded-lg border border-slate-200 text-[12px] font-semibold bg-white focus:outline-none focus:border-[color:var(--cf-primary)] focus:ring-1 focus:ring-[color:var(--cf-primary-border)]"
              data-testid="segment-personal-relation"
            >
              <option value="">— Sélectionner —</option>
              {PERSONAL_RELATIONS.map((r) => (
                <option key={r.key} value={r.key}>{r.label}</option>
              ))}
            </select>
          </Field>
          {form.personal_relation === "autre" && (
            <Field label="Précisez">
              <input
                type="text"
                value={form.personal_relation_other}
                onChange={(e) => setField("personal_relation_other", e.target.value)}
                placeholder="Précise la relation"
                className="w-full px-2 py-1.5 rounded-lg border border-slate-200 text-[12px] font-semibold bg-white focus:outline-none focus:border-[color:var(--cf-primary)] focus:ring-1 focus:ring-[color:var(--cf-primary-border)]"
                data-testid="segment-personal-relation-other"
              />
            </Field>
          )}
        </div>
      )}

      {cat === "non_renseigne" && (
        <div className="text-[11px] text-slate-500 font-semibold leading-snug" data-testid="segment-nr-hint">
          Catégorise cet apporteur pour activer les recommandations commerciales segmentées du briefing.
        </div>
      )}

      {/* Actions */}
      {dirty && (
        <div className="flex items-center gap-2 mt-3 pt-3 border-t border-slate-200">
          <button
            type="button"
            onClick={cancel}
            disabled={saving}
            className="px-3 py-1.5 rounded-lg text-[11px] font-extrabold text-slate-600 hover:bg-slate-100 disabled:opacity-50 inline-flex items-center gap-1"
            data-testid="segment-cancel"
          >
            <X size={11} weight="bold" />
            Annuler
          </button>
          <button
            type="button"
            onClick={save}
            disabled={saving}
            className="flex-1 px-3 py-1.5 rounded-lg text-[11px] font-extrabold bg-slate-900 text-white hover:bg-slate-800 disabled:opacity-60 inline-flex items-center justify-center gap-1.5"
            data-testid="segment-save"
          >
            <FloppyDisk size={12} weight="fill" />
            {saving ? "Enregistrement…" : "Enregistrer la segmentation"}
          </button>
        </div>
      )}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="text-[9px] font-bold uppercase tracking-widest text-slate-400 mb-1">{label}</div>
      {children}
    </label>
  );
}
